/**
 * REST helpers for live Q&A / announcements / polls (trainer).
 */

import { livekitAuthService as authService } from "../utils/authService";
import type {
  LiveAnnouncement,
  LivePoll,
  PollOption,
  QaItem,
  ChatAccess,
  ChatMessage,
  ChatThreadPeer,
} from "./liveCollaborationTypes";
import { makeChatThreadId } from "./liveCollaborationTypes";
import {
  appendCollabPageParams,
  COLLAB_PAGE_SIZE,
  type CollabPage,
  type CollabPageQuery,
} from "./collabHistory";
import {
  collabEtagKey,
  getCollabEtag,
  setCollabEtag,
  setCollabVersionAsEtag,
} from "./collabCacheEtag";
import { singleFlight } from "./singleFlight";

const lastQaBySession = new Map<string, QaItem[]>();
const lastAnnBySession = new Map<string, LiveAnnouncement[]>();
const lastChatByKey = new Map<string, ChatMessage[]>();

function etagIfCached(
  key: string,
  historical: boolean,
  cached: unknown[] | undefined,
): string {
  // Scroll-back and a first load with no cache still hit the API.
  if (historical || cached == null) return "";
  return getCollabEtag(key) || "";
}

function asParticipant(raw: unknown) {
  const r = (raw || {}) as Record<string, unknown>;
  return {
    identity: String(r.identity ?? ""),
    role: String(r.role ?? "student"),
    display_name: String(r.display_name ?? r.displayName ?? ""),
  };
}

export function normalizeQaItem(raw: Record<string, unknown>): QaItem {
  const answerRaw = raw.answer as Record<string, unknown> | null | undefined;
  return {
    qa_id: String(raw.qa_id ?? raw.qaId ?? ""),
    session_id: String(raw.session_id ?? raw.sessionId ?? ""),
    asked_by: asParticipant(raw.asked_by ?? raw.askedBy),
    question: String(raw.question ?? ""),
    status: (raw.status as QaItem["status"]) || "open",
    is_anonymous: Boolean(raw.is_anonymous ?? raw.isAnonymous),
    topic_id: raw.topic_id ? String(raw.topic_id) : null,
    topic_title: String(raw.topic_title ?? ""),
    answer: answerRaw
      ? {
          body: String(answerRaw.body ?? ""),
          answered_by: asParticipant(
            answerRaw.answered_by ?? answerRaw.answeredBy,
          ),
          answered_at: answerRaw.answered_at
            ? String(answerRaw.answered_at)
            : null,
        }
      : null,
    pinned: Boolean(raw.pinned),
    visible_to_all: Boolean(raw.visible_to_all ?? raw.visibleToAll),
    created_at: raw.created_at ? String(raw.created_at) : null,
  };
}

export function normalizeAnnouncement(
  raw: Record<string, unknown>,
): LiveAnnouncement {
  return {
    announcement_id: String(raw.announcement_id ?? raw.announcementId ?? ""),
    session_id: String(raw.session_id ?? raw.sessionId ?? ""),
    title: String(raw.title ?? ""),
    body: String(raw.body ?? ""),
    author: raw.author ? asParticipant(raw.author) : null,
    pinned: Boolean(raw.pinned),
    status: String(raw.status ?? "published"),
    created_at: raw.created_at ? String(raw.created_at) : null,
    published_at: raw.published_at ? String(raw.published_at) : null,
  };
}

export function normalizePoll(raw: Record<string, unknown>): LivePoll {
  const options: PollOption[] = Array.isArray(raw.options)
    ? (raw.options as Record<string, unknown>[]).map((o) => ({
        id: String(o.id ?? ""),
        text: String(o.text ?? ""),
        votes: Number(o.votes ?? 0),
      }))
    : [];
  const votes = Array.isArray(raw.votes)
    ? (raw.votes as Record<string, unknown>[]).map((v) => ({
        student_id: String(v.student_id ?? v.studentId ?? ""),
        student_name: String(v.student_name ?? v.studentName ?? ""),
        option_id: String(v.option_id ?? v.optionId ?? ""),
        voted_at: v.voted_at
          ? String(v.voted_at)
          : v.votedAt
            ? String(v.votedAt)
            : null,
      }))
    : [];
  return {
    poll_id: String(raw.poll_id ?? raw.pollId ?? ""),
    session_id: String(raw.session_id ?? raw.sessionId ?? ""),
    question: String(raw.question ?? ""),
    options,
    status: raw.status === "ended" ? "ended" : "active",
    total_votes: Number(raw.total_votes ?? raw.totalVotes ?? 0),
    missed_count: Number(raw.missed_count ?? raw.missedCount ?? 0),
    ends_at: Number(raw.ends_at ?? raw.endsAt ?? 0),
    created_by: raw.created_by ? asParticipant(raw.created_by) : null,
    my_vote_id: null,
    my_missed: false,
    created_at: raw.created_at ? String(raw.created_at) : null,
    votes,
  };
}

export async function fetchQaItems(
  sessionId: string,
  page?: CollabPageQuery,
): Promise<CollabPage<QaItem>> {
  // ---------------------------------------------------------------
  // step 1: One in-flight call. Older pages skip the list ETag.
  // ---------------------------------------------------------------
  const historical = Boolean(page?.before);
  const incremental = Boolean(page?.since) && !historical;
  const key = collabEtagKey("qa", sessionId);
  const flight = `qa:${sessionId}:${page?.since || ""}:${page?.before || ""}`;
  return singleFlight(flight, async () => {
    const etag = etagIfCached(key, historical, lastQaBySession.get(sessionId));
    const qs = new URLSearchParams();
    appendCollabPageParams(qs, {
      limit: COLLAB_PAGE_SIZE,
      ...page,
    });
    const res = await authService.get(
      `/session/sessions/${sessionId}/qa/${qs.toString() ? `?${qs.toString()}` : ""}`,
      {
        headers: etag ? { "If-None-Match": etag } : undefined,
        validateStatus: (s: number) => (s >= 200 && s < 300) || s === 304,
      },
    );
    // ---------------------------------------------------------------
    // step 2: 304 with since means no new rows; otherwise keep the cache
    // ---------------------------------------------------------------
    if (res.status === 304) {
      if (incremental || historical) return { items: [], hasMore: false };
      return { items: lastQaBySession.get(sessionId) || [], hasMore: true };
    }
    // ---------------------------------------------------------------
    // step 3: Store the new ETag and return this page
    // ---------------------------------------------------------------
    setCollabEtag(key, res.headers?.etag || res.headers?.ETag);
    if (res.data?.cache_version != null) {
      setCollabVersionAsEtag("qa", sessionId, Number(res.data.cache_version));
    }
    const rows = Array.isArray(res.data?.items) ? res.data.items : [];
    const items = rows.map((r: Record<string, unknown>) => normalizeQaItem(r));
    if (!incremental && !historical) lastQaBySession.set(sessionId, items);
    return { items, hasMore: Boolean(res.data?.has_more) };
  });
}

export async function patchQa(params: {
  sessionId: string;
  qaId: string;
  action:
    | "answer"
    | "dismiss"
    | "pin"
    | "unpin"
    | "show"
    | "hide";
  body?: string;
  trainerId: string;
  trainerName: string;
}): Promise<QaItem> {
  const res = await authService.patch(
    `/session/sessions/${params.sessionId}/qa/${params.qaId}/`,
    {
      action: params.action,
      body: params.body || null,
      trainer_id: params.trainerId,
      trainer_name: params.trainerName,
    },
  );
  return normalizeQaItem(res.data?.item || {});
}

export async function fetchAnnouncements(
  sessionId: string,
  page?: CollabPageQuery,
): Promise<CollabPage<LiveAnnouncement>> {
  // ---------------------------------------------------------------
  // step 1: One in-flight call. Older pages skip the list ETag.
  // ---------------------------------------------------------------
  const historical = Boolean(page?.before);
  const incremental = Boolean(page?.since) && !historical;
  const key = collabEtagKey("ann", sessionId);
  const flight = `ann:${sessionId}:${page?.since || ""}:${page?.before || ""}`;
  return singleFlight(flight, async () => {
    const etag = etagIfCached(
      key,
      historical,
      lastAnnBySession.get(sessionId),
    );
    const qs = new URLSearchParams();
    appendCollabPageParams(qs, {
      limit: COLLAB_PAGE_SIZE,
      ...page,
    });
    const res = await authService.get(
      `/session/sessions/${sessionId}/announcements/${
        qs.toString() ? `?${qs.toString()}` : ""
      }`,
      {
        headers: etag ? { "If-None-Match": etag } : undefined,
        validateStatus: (s: number) => (s >= 200 && s < 300) || s === 304,
      },
    );
    // ---------------------------------------------------------------
    // step 2: 304 with since means no new rows; otherwise keep the cache
    // ---------------------------------------------------------------
    if (res.status === 304) {
      if (incremental || historical) return { items: [], hasMore: false };
      return { items: lastAnnBySession.get(sessionId) || [], hasMore: true };
    }
    // ---------------------------------------------------------------
    // step 3: Store the new ETag and return this page
    // ---------------------------------------------------------------
    setCollabEtag(key, res.headers?.etag || res.headers?.ETag);
    if (res.data?.cache_version != null) {
      setCollabVersionAsEtag("ann", sessionId, Number(res.data.cache_version));
    }
    const rows = Array.isArray(res.data?.items) ? res.data.items : [];
    const items = rows.map((r: Record<string, unknown>) =>
      normalizeAnnouncement(r),
    );
    if (!incremental && !historical) lastAnnBySession.set(sessionId, items);
    return { items, hasMore: Boolean(res.data?.has_more) };
  });
}

export async function createAnnouncement(params: {
  sessionId: string;
  trainerId: string;
  trainerName: string;
  title: string;
  body: string;
  pinned?: boolean;
  announcementId?: string;
}): Promise<LiveAnnouncement> {
  const res = await authService.post(
    `/session/sessions/${params.sessionId}/announcements/`,
    {
      trainer_id: params.trainerId,
      trainer_name: params.trainerName,
      title: params.title,
      body: params.body,
      pinned: Boolean(params.pinned),
      announcement_id: params.announcementId || null,
    },
  );
  return normalizeAnnouncement(res.data?.item || {});
}

export async function patchAnnouncement(params: {
  sessionId: string;
  announcementId: string;
  action: "pin" | "unpin" | "archive";
}): Promise<LiveAnnouncement> {
  const res = await authService.patch(
    `/session/sessions/${params.sessionId}/announcements/${params.announcementId}/`,
    { action: params.action },
  );
  return normalizeAnnouncement(res.data?.item || {});
}

export async function fetchActivePoll(
  sessionId: string,
): Promise<LivePoll | null> {
  return singleFlight(`poll-active:${sessionId}`, async () => {
    const res = await authService.get(
      `/session/sessions/${sessionId}/polls/active/`,
    );
    if (!res.data?.poll) return null;
    return normalizePoll(res.data.poll);
  });
}

export async function createPoll(params: {
  sessionId: string;
  trainerId: string;
  trainerName: string;
  question: string;
  options: string[];
  durationSeconds?: number;
  pollId?: string;
}): Promise<LivePoll> {
  const res = await authService.post(
    `/session/sessions/${params.sessionId}/polls/`,
    {
      trainer_id: params.trainerId,
      trainer_name: params.trainerName,
      question: params.question,
      options: params.options.map((text, i) => ({
        id: `opt-${i + 1}`,
        text,
      })),
      duration_seconds: params.durationSeconds ?? 120,
      poll_id: params.pollId || null,
    },
  );
  return normalizePoll(res.data?.poll || {});
}

export async function endPoll(params: {
  sessionId: string;
  pollId: string;
}): Promise<LivePoll> {
  const res = await authService.post(
    `/session/sessions/${params.sessionId}/polls/${params.pollId}/end/`,
    {},
  );
  return normalizePoll(res.data?.poll || {});
}

export async function fetchPollHistory(sessionId: string): Promise<LivePoll[]> {
  const res = await authService.get(`/session/sessions/${sessionId}/polls/`);
  const rows = Array.isArray(res.data?.polls) ? res.data.polls : [];
  return rows.map((r: Record<string, unknown>) => normalizePoll(r));
}

export function normalizeChatMessage(
  raw: Record<string, unknown>,
): ChatMessage {
  const reactionsRaw = Array.isArray(raw.reactions) ? raw.reactions : [];
  return {
    message_id: String(raw.message_id ?? raw.messageId ?? ""),
    session_id: String(raw.session_id ?? raw.sessionId ?? ""),
    channel: raw.channel === "dm" ? "dm" : "public",
    thread_id: raw.thread_id ? String(raw.thread_id) : null,
    sender: asParticipant(raw.sender),
    recipient: raw.recipient ? asParticipant(raw.recipient) : null,
    body: String(raw.body ?? ""),
    created_at: raw.created_at ? String(raw.created_at) : null,
    like_count: Math.max(0, Number(raw.like_count ?? raw.likeCount ?? 0)),
    i_liked: Boolean(raw.i_liked ?? raw.iLiked),
    reactions: reactionsRaw
      .map((r) => {
        const row = r as Record<string, unknown>;
        return {
          emoji: String(row.emoji ?? ""),
          count: Math.max(0, Number(row.count ?? 0)),
          i_reacted: Boolean(row.i_reacted ?? row.iReacted),
        };
      })
      .filter((r) => r.emoji && r.count > 0),
  };
}

export function normalizeChatAccess(raw: Record<string, unknown>): ChatAccess {
  return {
    session_id: String(raw.session_id ?? ""),
    students_chat_enabled: Boolean(
      raw.students_chat_enabled ?? raw.studentsChatEnabled ?? true,
    ),
    students_group_chat_enabled: Boolean(
      raw.students_group_chat_enabled ?? raw.studentsGroupChatEnabled ?? true,
    ),
    students_can_message_trainer: Boolean(
      raw.students_can_message_trainer ??
        raw.studentsCanMessageTrainer ??
        true,
    ),
  };
}

export async function fetchChatAccess(sessionId: string): Promise<ChatAccess> {
  const res = await authService.get(
    `/session/sessions/${sessionId}/chat/access/`,
  );
  return normalizeChatAccess(res.data?.access || {});
}

/** One API hit for chat + Q&A + announcements + active poll. */
export async function fetchCollabBootstrap(params: {
  sessionId: string;
  trainerId?: string;
  since?: string;
}): Promise<{
  chatAccess: ChatAccess;
  chatPublic: ChatMessage[];
  chatThreads: ChatThreadPeer[];
  qa: QaItem[];
  announcements: LiveAnnouncement[];
  activePoll: LivePoll | null;
  chatPublicHasMore: boolean;
  chatDmHasMore: boolean;
  qaHasMore: boolean;
  announcementsHasMore: boolean;
}> {
  const qs = new URLSearchParams();
  if (params.trainerId) qs.set("trainer_id", params.trainerId);
  if (params.since) qs.set("since", params.since);
  qs.set("limit", String(COLLAB_PAGE_SIZE));
  const url = `/session/sessions/${params.sessionId}/collab/bootstrap/?${qs.toString()}`;
  const res = await authService.get(url);
  const data = res.data || {};
  const versions = (data.cache_versions || {}) as Record<string, number>;
  setCollabVersionAsEtag("chat_public", params.sessionId, versions.chat_public);
  setCollabVersionAsEtag("qa", params.sessionId, versions.qa);
  setCollabVersionAsEtag("ann", params.sessionId, versions.announcements);
  const chatPublic = Array.isArray(data.chat_public)
    ? data.chat_public.map((r: Record<string, unknown>) =>
        normalizeChatMessage(r),
      )
    : [];
  const qa = Array.isArray(data.qa)
    ? data.qa.map((r: Record<string, unknown>) => normalizeQaItem(r))
    : [];
  const announcements = Array.isArray(data.announcements)
    ? data.announcements.map((r: Record<string, unknown>) =>
        normalizeAnnouncement(r),
      )
    : [];
  lastChatByKey.set(`chat_public:${params.sessionId}`, chatPublic);
  lastQaBySession.set(params.sessionId, qa);
  lastAnnBySession.set(params.sessionId, announcements);
  return {
    chatAccess: normalizeChatAccess(data.chat_access || {}),
    chatPublic,
    chatThreads: Array.isArray(data.chat_threads)
      ? data.chat_threads.map((r: Record<string, unknown>) => ({
          thread_id: String(r.thread_id ?? ""),
          peer: asParticipant(r.peer),
        }))
      : [],
    qa,
    announcements,
    activePoll: data.active_poll
      ? normalizePoll(data.active_poll as Record<string, unknown>)
      : null,
    chatPublicHasMore: Boolean(data.chat_public_has_more),
    chatDmHasMore: Boolean(data.chat_dm_has_more),
    qaHasMore: Boolean(data.qa_has_more),
    announcementsHasMore: Boolean(data.announcements_has_more),
  };
}

export async function patchChatAccess(params: {
  sessionId: string;
  students_chat_enabled?: boolean;
  students_group_chat_enabled?: boolean;
  students_can_message_trainer?: boolean;
}): Promise<ChatAccess> {
  const res = await authService.patch(
    `/session/sessions/${params.sessionId}/chat/access/`,
    {
      students_chat_enabled: params.students_chat_enabled,
      students_group_chat_enabled: params.students_group_chat_enabled,
      students_can_message_trainer: params.students_can_message_trainer,
    },
  );
  return normalizeChatAccess(res.data?.access || {});
}

export async function fetchChatMessages(params: {
  sessionId: string;
  channel?: "public" | "dm";
  threadId?: string;
  /** Trainer private inbox: every saved student thread, no thread_id. */
  trainerId?: string;
  since?: string;
  before?: string;
  limit?: number;
}): Promise<CollabPage<ChatMessage>> {
  // ---------------------------------------------------------------
  // step 1: Recent page first. Scroll-back (before) skips the list ETag.
  // ---------------------------------------------------------------
  const channel = params.channel || "public";
  const kind = channel === "dm" ? "chat_dm" : "chat_public";
  const inbox = channel === "dm" && !params.threadId && Boolean(params.trainerId);
  const cacheKey = collabEtagKey(
    kind,
    params.sessionId,
    params.threadId || (inbox ? `inbox:${params.trainerId}` : ""),
  );
  const historical = Boolean(params.before);
  const incremental = Boolean(params.since) && !historical;
  const flight = `chat:${cacheKey}:${params.since || ""}:${params.before || ""}`;
  return singleFlight(flight, async () => {
    const etag = etagIfCached(
      cacheKey,
      historical,
      lastChatByKey.get(cacheKey),
    );
    const qs = new URLSearchParams();
    qs.set("channel", channel);
    if (params.threadId) qs.set("thread_id", params.threadId);
    else if (inbox && params.trainerId) qs.set("trainer_id", params.trainerId);
    appendCollabPageParams(qs, {
      since: params.since,
      before: params.before,
      limit: params.limit || COLLAB_PAGE_SIZE,
    });
    const res = await authService.get(
      `/session/sessions/${params.sessionId}/chat/messages/?${qs.toString()}`,
      {
        headers: etag ? { "If-None-Match": etag } : undefined,
        validateStatus: (s: number) => (s >= 200 && s < 300) || s === 304,
      },
    );
    // ---------------------------------------------------------------
    // step 2: 304 with since means no new messages; otherwise keep the cache
    // ---------------------------------------------------------------
    if (res.status === 304) {
      if (incremental || historical) return { items: [], hasMore: false };
      return { items: lastChatByKey.get(cacheKey) || [], hasMore: true };
    }
    // ---------------------------------------------------------------
    // step 3: Store the new ETag and return this page
    // ---------------------------------------------------------------
    setCollabEtag(cacheKey, res.headers?.etag || res.headers?.ETag);
    if (res.data?.cache_version != null && kind === "chat_public") {
      setCollabVersionAsEtag(
        "chat_public",
        params.sessionId,
        Number(res.data.cache_version),
      );
    }
    const rows = Array.isArray(res.data?.items) ? res.data.items : [];
    const items = rows.map((r: Record<string, unknown>) =>
      normalizeChatMessage(r),
    );
    if (!incremental && !historical) lastChatByKey.set(cacheKey, items);
    return { items, hasMore: Boolean(res.data?.has_more) };
  });
}

export async function fetchChatThreads(params: {
  sessionId: string;
  trainerId: string;
}): Promise<ChatThreadPeer[]> {
  const res = await authService.get(
    `/session/sessions/${params.sessionId}/chat/threads/?trainer_id=${encodeURIComponent(params.trainerId)}`,
  );
  const rows = Array.isArray(res.data?.threads) ? res.data.threads : [];
  return rows.map((r: Record<string, unknown>) => ({
    thread_id: String(r.thread_id ?? ""),
    peer: asParticipant(r.peer),
  }));
}

export async function postChatMessage(params: {
  sessionId: string;
  senderId: string;
  senderName: string;
  senderRole: "trainer" | "student";
  body: string;
  channel: "public" | "dm";
  peerId?: string;
  peerName?: string;
  peerRole?: "trainer" | "student";
  messageId?: string;
}): Promise<ChatMessage> {
  const res = await authService.post(
    `/session/sessions/${params.sessionId}/chat/messages/`,
    {
      sender_id: params.senderId,
      sender_name: params.senderName,
      sender_role: params.senderRole,
      body: params.body,
      channel: params.channel,
      peer_id: params.peerId || null,
      peer_name: params.peerName || "",
      peer_role: params.peerRole || "student",
      message_id: params.messageId || null,
    },
  );
  return normalizeChatMessage(res.data?.message || {});
}

export async function toggleChatLike(params: {
  sessionId: string;
  messageId: string;
  userId: string;
  userName?: string;
}): Promise<ChatMessage> {
  const res = await authService.post(
    `/session/sessions/${params.sessionId}/chat/messages/${params.messageId}/like/`,
    {
      user_id: params.userId,
      user_name: params.userName || "",
    },
  );
  return normalizeChatMessage(res.data?.message || {});
}

export async function toggleChatReaction(params: {
  sessionId: string;
  messageId: string;
  userId: string;
  userName?: string;
  emoji: string;
}): Promise<ChatMessage> {
  const res = await authService.post(
    `/session/sessions/${params.sessionId}/chat/messages/${params.messageId}/react/`,
    {
      user_id: params.userId,
      user_name: params.userName || "",
      emoji: params.emoji,
    },
  );
  return normalizeChatMessage(res.data?.message || {});
}

export { makeChatThreadId };

